import React, { useEffect, useReducer, useState } from 'react';
import {
  Backdrop,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
  Autocomplete,
  Stack,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  isAlphaKey,
  isAlphaNumericChar,
  isValidCityChar,
  isValidPasswordChar,
  newUserInputValidate,
} from '../../mobile/mobileComponents/mobileUserAccountLogin/userAccoountHook';
import { signUp } from '../../apiCalls/urls/urlFunction';
import { actionNotifier } from '../../components/tostify';
import ErrorMsg from '../../components/errorMsg/ErrorMsg';
import BasicBtn from '../../components/button/BasicBtn';
import { NavLink, useNavigate } from 'react-router-dom';
import { getBaseUrl } from '../../shared/axios';

// ✅ Initial reducer state
const initialState = {
  formData: {
    name: '',
    email: '',
    mobile: '',
    password: '',
    referral: '',
    gender: '',
    city: '',
    state_name: '',
    termsAccepted: false,
  },
  errors: {
    nameError: '',
    emailError: '',
    mobileError: '',
    passwordError: '',
    referralError: '',
    genderError: '',
    cityError: '',
    state_nameError: '',
    termsAcceptedError: '',
  },
};

// ✅ Reducer function
function signupFormReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.value,
        },
        errors: {
          ...state.errors,
          [`${action.field}Error`]: '', // Clear error on input change
        },
      };
    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [`${action.field}Error`]: action.message,
        },
      };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
}

function StateCitySelectionComponent({ onStateSelect, onCitySelect, stateError, cityError }) {
  const [stateOpen, setStateOpen] = React.useState(false);
  const [cityOpen, setCityOpen] = React.useState(false);

  const [stateLoading, setStateLoading] = React.useState(false);
  const [cityLoading, setCityLoading] = React.useState(false);

  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);

  const [selectedState, setSelectedState] = React.useState(null);
  const [selectedCity, setSelectedCity] = React.useState(null);

  // -----------------------------------------
  // Get States
  // -----------------------------------------
  const callAllStatesAPI = async () => {
    try {
      setStateLoading(true);

      const response = await fetch(
        `${getBaseUrl()}get-all-cities`,
        {
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            per_page: 100,
            state_id: null,
          }),
          method: "POST",
        }
      );

      const modifiedData = await response.json();

      if (
        modifiedData?.status === "success" &&
        modifiedData?.data?.length > 0
      ) {
        setStates(modifiedData.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
    } finally {
      setStateLoading(false);
    }
  };

  // -----------------------------------------
  // Get Cities based on selected State
  // -----------------------------------------
  const callCitiesAPI = async (stateId) => {
    if (!stateId) return;

    try {
      setCityLoading(true);

      const response = await fetch(
        `${getBaseUrl()}get-all-cities`,
        {
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            per_page: 100,
            state_id: stateId,
          }),
          method: "POST",
        }
      );

      const modifiedData = await response.json();

      if (
        modifiedData?.status === "success" &&
        modifiedData?.data?.length > 0
      ) {
        setCities(modifiedData.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setCityLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      {/* ================= STATE ================= */}
      <Autocomplete
        id="states"
        fullWidth
        value={selectedState}
        open={stateOpen}
        onOpen={() => {
          setStateOpen(true);

          // API call every time State dropdown opens
          callAllStatesAPI();
        }}
        onClose={() => {
          setStateOpen(false);
        }}
        onChange={(event, newValue) => {
          setSelectedState(newValue);
          if (onStateSelect) {
            onStateSelect(newValue?.state_name || "");
          }

          // Important:
          // Clear city when state changes
          setSelectedCity(null);
          if (onCitySelect) {
            onCitySelect("");
          }
          setCities([]);
        }}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id
        }
        getOptionLabel={(option) => option?.state_name || ""}
        options={states}
        loading={stateLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="State"
            error={!!stateError}
            helperText={stateError}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {stateLoading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  )}

                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {/* ================= CITY ================= */}
      <Autocomplete
        id="cities"
        fullWidth
        value={selectedCity}
        disabled={!selectedState}
        open={cityOpen}
        onOpen={() => {
          setCityOpen(true);

          // Call API using selected state ID
          if (selectedState?.id) {
            callCitiesAPI(selectedState.id);
          }
        }}
        onClose={() => {
          setCityOpen(false);
        }}
        onChange={(event, newValue) => {
          setSelectedCity(newValue);
          if (onCitySelect) {
            onCitySelect(newValue?.city_name || "");
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id
        }
        getOptionLabel={(option) =>
          option?.city_name || ""
        }
        options={cities}
        loading={cityLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City"
            error={!!cityError}
            helperText={cityError}
            placeholder={
              selectedState
                ? "Select City"
                : "Select State first"
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {cityLoading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  )}

                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </Stack>
  );
}

const Signup = ({ handleUserForm, setIsUserAuthentic }) => {
  const [state, dispatch] = useReducer(signupFormReducer, initialState);
  const { formData, errors } = state;

  const [showPassword, setShowPassword] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');
  const navigate = useNavigate();

  // ✅ Show alert utility
  const showAlert = (message, severity = 'error') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // ✅ Handle input changes for all fields
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    dispatch({ type: 'UPDATE_FIELD', field: name, value: fieldValue });
  };

  // ✅ Special handler for mobile/referral input to restrict to digits
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/\D/g, '');

    if (value !== sanitizedValue) {
      dispatch({ type: 'SET_ERROR', field: name, message: 'Only numeric characters are allowed.' });
    } else {
      dispatch({ type: 'SET_ERROR', field: name, message: '' });
    }

    handleChange({
      target: { name, value: sanitizedValue, type: 'text' },
    });
  };

  // ✅ Handle password visibility
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (e) => e.preventDefault();

  // ✅ Send API request for signup
  const handleUserRegister = async (userDetails) => {
    const payload = {
      name: userDetails?.nameError?.title,
      email: userDetails?.emailError?.title,
      mobile: userDetails?.mobileError?.title,
      gender: userDetails?.genderError?.title,
      password: userDetails?.passwordError?.title,
      parent_id: userDetails?.referralError?.title,
      city: formData.city,
      state_name: formData.state_name,
    };

    setSignUpLoading(true);

    try {
      const signupResponse = await signUp(payload);

      if (signupResponse?.data?.status) {
        actionNotifier.success('Otp Sent Successful', { position: 'top-left', autoClose: 3000 });
        setIsUserAuthentic({
          mobile: payload?.mobile,
          status: signupResponse?.data?.status,
          isLoginForm: false,
        });
        handleUserForm('USERREGISTERFORMFILLSUCCESSFUL');
        dispatch({ type: 'RESET_FORM' });
        return;
      } else {
        showAlert(signupResponse?.message || 'Signup failed');
      }
    } catch (error) {
      showAlert('Something went wrong. Please try again later.');
    } finally {
      setSignUpLoading(false);
    }
  };

  // ✅ Handle form submission and validate
  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = newUserInputValidate(formData);

    let hasError = validation?.isAnyError;

    if (!formData.state_name) {
      hasError = true;
      dispatch({ type: 'SET_ERROR', field: 'state_name', message: 'State is required.' });
    }
    if (!formData.city) {
      hasError = true;
      dispatch({ type: 'SET_ERROR', field: 'city', message: 'City is required.' });
    }

    if (hasError) {
      // Dispatch each error to reducer
      Object.entries(validation).forEach(([key, val]) => {
        if (typeof val === 'object' && val !== null && val?.status) {
          const fieldName = key.replace('Error', '');
          dispatch({ type: 'SET_ERROR', field: fieldName, message: val.message });
        }
      });
      showAlert('Please correct the highlighted errors before submitting.');
    } else {
      handleUserRegister(validation);
    }
  };

  const handleUserInputKey = (e) => {
    const { name, value } = e.target;
    const lastChar = value.slice(-1);
    const prevChar = value.slice(-2, -1);

    switch (name) {
      case 'name':
        const isAlpha = isAlphaKey(lastChar);
        const isSingleSpace = lastChar === ' ' && prevChar !== ' ';

        if (isAlpha || isSingleSpace || value === '') {
          handleChange(e);
        }
        break;
      case 'password':
        const isValidPassword = isValidPasswordChar(lastChar);

        if (isValidPassword || value === '') {
          handleChange(e);
        }
        break;
      case 'referral':
        const isValidReferral = isAlphaNumericChar(lastChar);

        if (isValidReferral || value === '') {
          handleChange(e);
        }
        break;
      case 'city':
        const isAlphaOrCommaOrSpace = isValidCityChar(lastChar);
        const isDoubleSpace = lastChar === ' ' && prevChar === ' ';
        const isDoubleComma = lastChar === ',' && prevChar === ',';
        const isSpaceAroundComma =
          (lastChar === ' ' && prevChar === ',') || (lastChar === ',' && prevChar === ' ');

        const isValid =
          isAlphaOrCommaOrSpace && !isDoubleSpace && !isDoubleComma && !isSpaceAroundComma;

        if (isValid || value === '') {
          handleChange(e);
        }
        break;
      default:
        handleChange(e);
        break;
    }
  };

  return (
    <Box component={'div'} sx={{ mb: 3 }}>
      <ErrorMsg
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        severity={alertSeverity}
        height={1}
      />

      <FormControl component="form" method="post" onSubmit={handleSubmit} fullWidth>
        {/* Name */}
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formData.name}
          // onChange={handleChange}
          onChange={handleUserInputKey}
          error={!!errors.nameError}
          helperText={errors.nameError}
          margin="normal"
          required
          sx={{ backgroundColor: 'white.main', border: 0 }}
        />

        {/* Mobile */}
        <TextField
          fullWidth
          label="Mobile Number"
          name="mobile"
          value={formData.mobile}
          onChange={handleUserInputKey}
          error={!!errors.mobileError}
          helperText={errors.mobileError}
          margin="normal"
          inputProps={{ maxLength: 10 }}
          required
          sx={{ backgroundColor: 'white.main', border: 0 }}
        />

        {/* Email */}
        <TextField
          fullWidth
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleUserInputKey}
          error={!!errors.emailError}
          helperText={errors.emailError}
          margin="normal"
          required
          sx={{ backgroundColor: 'white.main', border: 0 }}
        />

        {/* Password */}
        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleUserInputKey}
          error={!!errors.passwordError}
          helperText={errors.passwordError}
          margin="normal"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: 'white.main', border: 0 }}
        />

        {/* Referral Code */}
        <TextField
          fullWidth
          label="Referral Code (Optional)"
          name="referral"
          value={formData.referral}
          onChange={handleUserInputKey}
          error={!!errors.referralError}
          helperText={errors.referralError}
          margin="normal"
          inputProps={{ maxLength: 25 }}
          sx={{ backgroundColor: 'white.main', border: 0 }}
        />

        {/* Gender */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Select Gender:
        </Typography>
        <RadioGroup row name="gender" value={formData.gender} onChange={handleChange}>
          <FormControlLabel value="female" control={<Radio color="secondary" />} label="Female" />
          <FormControlLabel value="male" control={<Radio color="secondary" />} label="Male" />
          <FormControlLabel value="other" control={<Radio color="secondary" />} label="Other" />
        </RadioGroup>
        {errors.genderError && <FormHelperText error>{errors.genderError}</FormHelperText>}

        <Box sx={{ mt: 2, mb: 1 }}>
          <StateCitySelectionComponent
            onStateSelect={(stateName) => dispatch({ type: 'UPDATE_FIELD', field: 'state_name', value: stateName })}
            onCitySelect={(cityName) => dispatch({ type: 'UPDATE_FIELD', field: 'city', value: cityName })}
            stateError={errors.state_nameError}
            cityError={errors.cityError}
          />
        </Box>

        {/* Terms & Conditions */}
        <FormControlLabel
          control={<Checkbox name="termsAccepted" checked={true} color="secondary" />}
          label="I hereby authorise to send notifications via SMS, Email, RCS and others as per Terms of Service | Privacy Policy * "
        />
        {errors.termsAcceptedError && (
          <FormHelperText error>{errors.termsAcceptedError}</FormHelperText>
        )}

        <BasicBtn type="submit" name="Sign Up" color="secondary" smColor="primary" sx={{ mt: 3 }} />
      </FormControl>

      {/* Loading Spinner */}
      <Backdrop sx={{ color: '#fff', zIndex: 999 }} open={signUpLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          pt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: 'textPrimary.main',
            fontWeight: 400,
            mb: 2,
            textAlign: 'center',
          }}
        >
          Don't have an account?
        </Typography>

        <Button
          variant="outlined"
          fullWidth
          aria-label="Navigate to Login page"
          sx={{
            borderRadius: 6,
            py: 1,
            fontWeight: 600,
            fontSize: '1.125rem',
            color: 'secondary.main',
            borderColor: 'secondary.main',
            textTransform: 'none', // optional: to keep "Sign Up" casing
          }}
          onClick={() => navigate('/login', { replace: true })}
        >
          Log In
        </Button>
      </Box>
    </Box>
  );
};

export default Signup;
